import type { AiInterviewSessionListRow } from '@core/supabase/services/aiInterviewSessionService';
import type { AssessmentScores } from '@core/ai/interviewAssessment';
import { computeCompositeScore } from '@core/ai/interviewAssessment';

export type ReviewerSessionStatusKind = 'scheduled' | 'in_progress' | 'completed' | 'assessed' | 'cancelled';

export function normalizeAssessmentScores(raw: unknown): AssessmentScores | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out: AssessmentScores = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const n = Number(v);
    if (Number.isFinite(n)) out[k] = n;
  }
  return Object.keys(out).length > 0 ? out : null;
}

export function hasMeaningfulAssessment(assessedAt: string | null | undefined, scores: AssessmentScores | null): boolean {
  if (assessedAt && String(assessedAt).trim().length > 0) return true;
  return scores != null && Object.keys(scores).length > 0;
}

/** Display date for reviewer list: calendar slot when set, else session created. */
export function reviewerSessionDateIso(row: Pick<AiInterviewSessionListRow, 'pipeline_interview_event' | 'created_at'>): string {
  const slot = row.pipeline_interview_event?.scheduled_at?.trim();
  if (slot) return slot;
  return row.created_at;
}

export function reviewerListScoreDisplay(
  scores: AssessmentScores | null,
  assessedAt: string | null | undefined
): string {
  if (!hasMeaningfulAssessment(assessedAt, scores) || !scores) return '—';
  return String(computeCompositeScore(scores));
}

export function resolveReviewerSessionStatus(
  sessionStatus: string,
  assessedAt: string | null | undefined,
  scores: AssessmentScores | null
): ReviewerSessionStatusKind {
  const st = (sessionStatus ?? '').trim().toLowerCase();
  if (st === 'cancelled') return 'cancelled';
  if (st === 'in_progress') return 'in_progress';
  if (st === 'completed') {
    return hasMeaningfulAssessment(assessedAt, scores) ? 'assessed' : 'completed';
  }
  return 'scheduled';
}

const CHIP_BASE =
  'text-xs px-2 py-0.5 rounded-full font-medium inline-flex items-center border border-[color-mix(in_srgb,var(--vscode-border-primary)_70%,transparent)]';

export function reviewerStatusChipClass(kind: ReviewerSessionStatusKind): string {
  switch (kind) {
    case 'scheduled':
      return `${CHIP_BASE} bg-[var(--vscode-bg-secondary)] vscode-text-secondary`;
    case 'in_progress':
      return `${CHIP_BASE} bg-[color-mix(in_srgb,var(--vscode-text-link)_12%,var(--vscode-bg-secondary))] vscode-text-primary`;
    case 'completed':
      return `${CHIP_BASE} bg-[var(--vscode-bg-secondary)] vscode-text-primary`;
    case 'assessed':
      return `${CHIP_BASE} bg-[color-mix(in_srgb,var(--vscode-text-link)_8%,var(--vscode-bg-secondary))] vscode-text-primary`;
    case 'cancelled':
    default:
      return `${CHIP_BASE} opacity-80 vscode-text-tertiary`;
  }
}

export function reviewerStatusChipLabel(kind: ReviewerSessionStatusKind): string {
  switch (kind) {
    case 'scheduled':
      return 'Scheduled';
    case 'in_progress':
      return 'In progress';
    case 'completed':
      return 'Completed';
    case 'assessed':
      return 'Assessed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return 'Scheduled';
  }
}
